<template>
  <div
    v-show="true"
    id="modalValidationBirdSpro"
    class="modal fade"
    data-bs-backdrop="static"
    data-bs-keyboard="false"
    tabindex="-1"
    aria-labelledby="modalValidationBirdSproMessage"
    aria-hidden="true"
  >
    <div class="modal-dialog text-start">
      <div class="modal-content">
        <div class="modal-header">
          <h1 id="modalValidationBirdSproMessage" class="modal-title fs-5">
            Emissão de Certificado
          </h1>
        </div>
        <div class="modal-body py-4">
          <p class="fs-6">
            Abra seu aplicativo Bird ID no seu celular e digite o código OTP
            referente ao CPF abaixo para prosseguir com a emissão do seu
            Certificado Digital.
          </p>
          <div class="d-flex">
            <Icon name="id-card" class="me-2" style="margin-top: 2px" />
            <p class="fw-600 caption">CPF: {{ model.cpf }}</p>
          </div>
          <Input
            v-model="model.otp"
            label="OTP * (Bird ID)"
            icon="address-card"
            placeholder="Digite apenas números"
            is-numeric
            :mask="['### ###', '']"
            :error-log="error"
          />
          <div class="mt-4">
            <div class="form-check text-start">
              <input
                id="default-checkbox"
                v-model="model.termos"
                class="form-check-input form-check-input--success"
                type="checkbox"
              />
              <span
                class="form-check-label me-1 caption-small"
                for="default-checkbox"
              >
                Li e concordo com a
              </span>
              <a
                class="link-info text-decoration-underline caption-small"
                href="/PoliticadePrivacidade.pdf"
                target="_blank"
                >política de privacidade
              </a>
            </div>
          </div>
        </div>

        <div class="modal-footer border-top-1">
          <button
            class="btn modal-footer_btn-process"
            :class="!isValid ? 'btn-secondary' : 'btn-primary'"
            type="button"
            @click="handleNextStep"
          >
            <span class="me-2">Avançar</span>
            <Icon name="arrow-right" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import { Modal } from 'bootstrap'
  import Input from '../Atoms/Input.vue'
  import Icon from '../Atoms/Icon.vue'
  import { mapActions, mapState } from 'pinia'
  import { useDynamicForms } from '@/store/Pinia/useDynamicForms.store'
  import { useModalLoading } from '@/store/Pinia/useModalLoading.store'
  import { ApplyMaskToCPF, RemoveMaskCPF } from '@/utils/formatDocument'
  import loginController from '@/infra/BirdId/controllers/login.controller'
  import { AxiosError } from 'axios'
  import saveDataVoucher from '@/utils/saveDataVoucher'
  import { useAR } from '@/store/Pinia/useAR.store'
  import { useSolicitation } from '@/store/Pinia/useSolicitation.store'
  import { useOeRecaptcha } from '@/utils/useOeRecaptcha'

  export default defineComponent({
    name: 'ModalValidationBirdSpro',
    components: {
      Input,
      Icon,
    },
    props: {
      hotreload: {
        type: Function,
        required: true,
      },
    },
    setup() {
      return {
        oeRecaptcha: useOeRecaptcha(),
      }
    },
    data: () => ({
      model: {
        cpf: '',
        otp: '',
        termos: false,
      },
      modal: null as any,
      error: '',
    }),
    computed: {
      ...mapState(useAR, ['arData', 'getSubdomain']),
      ...mapState(useSolicitation, ['getSolicitation']),
      isValid() {
        return (
          !!this.model.termos &&
          this.model.otp !== '' &&
          this.model.otp.length === 7
        )
      },
    },
    methods: {
      ...mapActions(useDynamicForms, ['setCNPJ', 'setFormData']),
      ...mapActions(useModalLoading, ['setIsLoading']),
      modalOpen() {
        this.modal.show()
      },
      modalClose() {
        this.modal.hide()
      },
      async handleNextStep() {
        try {
          if (!this.isValid) {
            this.error = 'Preencha todos os campos'
            return
          }

          setTimeout(() => {
            this.setIsLoading({
              isLoading: true,
              title: 'Aguarde enquanto localizamos seu Voucher/Pedido',
              image: 'zoom',
              message: '',
            })
          }, 100)

          const recaptchaToken = await this.oeRecaptcha.getToken('loginwoe')

          const payload = await loginController.validateVoucherSPRO({
            username: RemoveMaskCPF(this.model.cpf),
            otp: this.model.otp,
            data: this.getSolicitation.encryptedData,
            arsubdomain: this.getSubdomain,
            recaptcha_token: recaptchaToken,
          })

          saveDataVoucher(payload)

          const { formData } = useDynamicForms()

          this.setCNPJ(payload.dataDecrypted?.cnpj || '')

          this.setFormData({
            ...formData,
            nome: payload.nome,
            cpf: this.model.cpf,
          })

          this.hotreload()

          setTimeout(() => {
            this.modalClose()
          }, 200)
        } catch (error) {
          const err = error as AxiosError<{ message: string } | any>
          const isLinkInvalid =
            err?.response?.status === 403 &&
            err?.response?.data?.code === 'BAD_ORDER_STATUS'

          if (!isLinkInvalid && err?.response && err?.response?.data) {
            this.error = err.response.data.message || 'Erro ao validar o OTP'
          }

          if (isLinkInvalid) {
            this.modalClose()
            setTimeout(() => this.$router.push({ name: 'BadSproLink' }), 1000)
          }
        } finally {
          this.setIsLoading({ isLoading: false })
        }
      },
    },
    mounted() {
      const forms = useDynamicForms()

      const modal = document.getElementById(
        'modalValidationBirdSpro',
      ) as HTMLElement
      this.modal = new Modal(modal, {})

      this.model.cpf = ApplyMaskToCPF(forms.formData.cpf)
    },
  })
</script>

<style lang="stylus" scoped>

  .img-wait
    max-width: 190px
    max-height 190px
  .img-zoom
    max-width: 190px
    max-height 190px

  .btn-modal
    opacity: 0

  #modalValidationBirdSpro
    .modal-dialog
      max-width: 400px

  @media screen and (max-width: 576px)
    .modal-dialog
      height: auto
      margin: 0.5rem
    .modal-content
      border-radius: 0.5rem
</style>
